import {AbsoluteFill, Easing, interpolate, useCurrentFrame} from 'remotion';
import {MetricHeader} from './components/MetricHeader';
import {Timeline} from './components/Timeline';
import {TrendChart} from './components/TrendChart';
import {dataset} from './data';
import {fontFamily, theme} from './theme';

export const VIDEO = {
  width: 1920,
  height: 1080,
  fps: 60,
  durationInFrames: 3600,
  introEnd: 180,
  playbackEnd: 3180,
} as const;

const WINDOW_SIZE = 52;
const numberFormatter = new Intl.NumberFormat('zh-CN');

const smoothstep = (value: number) => value * value * (3 - 2 * value);

export const WorkTimeline = () => {
  const frame = useCurrentFrame();
  const {rows, summary} = dataset;
  const playbackProgress = interpolate(
    frame,
    [VIDEO.introEnd, VIDEO.playbackEnd],
    [0, rows.length - 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.linear},
  );
  const outroProgress = interpolate(
    frame,
    [VIDEO.playbackEnd, VIDEO.playbackEnd + 150],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.inOut(Easing.cubic)},
  );

  const playbackDomainEnd = Math.max(WINDOW_SIZE - 1, playbackProgress);
  const playbackDomainStart = Math.max(0, playbackDomainEnd - (WINDOW_SIZE - 1));
  const finalWindowStart = rows.length - WINDOW_SIZE;
  const domainStart = interpolate(outroProgress, [0, 1], [finalWindowStart, 0]);
  const domainEnd = interpolate(outroProgress, [0, 1], [rows.length - 1, rows.length - 1]);
  const activeDomainStart = frame < VIDEO.playbackEnd ? playbackDomainStart : domainStart;
  const activeDomainEnd = frame < VIDEO.playbackEnd ? playbackDomainEnd : domainEnd;

  const currentFloor = Math.floor(playbackProgress);
  const currentCeil = Math.min(rows.length - 1, currentFloor + 1);
  const fractional = smoothstep(playbackProgress - currentFloor);
  const currentRow = rows[Math.min(rows.length - 1, Math.round(playbackProgress))];
  const currentSessions = interpolate(
    fractional,
    [0, 1],
    [rows[currentFloor].sessions, rows[currentCeil].sessions],
  );
  const currentMinutes = interpolate(
    fractional,
    [0, 1],
    [rows[currentFloor].minutes, rows[currentCeil].minutes],
  );
  const headerSessions = interpolate(
    outroProgress,
    [0, 1],
    [currentSessions, summary.totals.sessions],
  );
  const headerMinutes = interpolate(
    outroProgress,
    [0, 1],
    [currentMinutes, summary.totals.minutes],
  );
  const mainOpacity = interpolate(frame, [100, 170], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const introOpacity = interpolate(frame, [0, 45, 130, VIDEO.introEnd], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const introScale = interpolate(frame, [0, VIDEO.introEnd], [0.96, 1.02], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const dateLabel =
    outroProgress > 0.5
      ? `${summary.startDate.replaceAll('-', '.')} — ${summary.endDate.replaceAll('-', '.')}`
      : currentRow.dateLabel;

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(circle at 50% 36%, #0A2339 0%, ${theme.background} 40%, ${theme.backgroundDeep} 100%)`,
        overflow: 'hidden',
        color: theme.text,
      }}
    >
      <AbsoluteFill
        style={{
          opacity: 0.34,
          backgroundImage:
            'linear-gradient(rgba(43, 88, 122, 0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(43, 88, 122, 0.16) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'linear-gradient(to bottom, black, transparent 86%)',
        }}
      />
      <AbsoluteFill
        style={{
          opacity: mainOpacity,
          filter: `blur(${interpolate(introOpacity, [0, 1], [0, 4])}px)`,
        }}
      >
        <MetricHeader
          frame={frame}
          dateLabel={dateLabel}
          sessions={headerSessions}
          minutes={headerMinutes}
          averageSessions={summary.averages.sessions}
          averageMinutes={summary.averages.minutes}
          outroProgress={outroProgress}
        />

        <div style={{position: 'absolute', left: 96, top: 342, width: 1728}}>
          <TrendChart
            rows={rows}
            metric="sessions"
            label="工作会话次数"
            unit="每周 / 次"
            color={theme.sessions}
            maxValue={3000}
            peak={summary.peaks.sessions}
            dataProgress={playbackProgress}
            domainStart={activeDomainStart}
            domainEnd={activeDomainEnd}
            frame={frame}
            outroProgress={outroProgress}
          />
          <div style={{height: 22}} />
          <TrendChart
            rows={rows}
            metric="minutes"
            label="花费时间"
            unit="每周 / 分钟"
            color={theme.minutes}
            maxValue={1400}
            peak={summary.peaks.minutes}
            dataProgress={playbackProgress}
            domainStart={activeDomainStart}
            domainEnd={activeDomainEnd}
            frame={frame}
            outroProgress={outroProgress}
            fillArea
          />
        </div>

        <div style={{position: 'absolute', left: 96, top: 860, width: 1728}}>
          <Timeline
            rows={rows}
            filteredCount={summary.filteredCount}
            frame={frame}
            dataProgress={playbackProgress}
            domainStart={activeDomainStart}
            domainEnd={activeDomainEnd}
            outroProgress={outroProgress}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            left: 98,
            bottom: 36,
            color: theme.muted,
            fontFamily,
            fontSize: 15,
            letterSpacing: 1.2,
          }}
        >
          数据跨度 {summary.count} 周 · 会话次数 ≥ {summary.filter.minSessions}
        </div>
      </AbsoluteFill>

      <AbsoluteFill
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          opacity: introOpacity,
          transform: `scale(${introScale})`,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: 96,
            height: 2,
            marginBottom: 28,
            background: theme.sessions,
            boxShadow: `0 0 24px ${theme.sessions}`,
          }}
        />
        <div
          style={{
            fontFamily,
            fontSize: 76,
            fontWeight: 650,
            letterSpacing: 4,
            textShadow: '0 8px 36px rgba(0, 0, 0, 0.5)',
          }}
        >
          工作数据时间轴
        </div>
        <div
          style={{
            marginTop: 24,
            fontFamily,
            fontSize: 24,
            letterSpacing: 3,
            color: theme.muted,
          }}
        >
          {summary.startDate.slice(0, 4)} — {summary.endDate.slice(0, 4)} · {numberFormatter.format(summary.count)} 周
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
